import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { SectionEditor } from "./SectionEditor";
import { useToast } from "@/components/ui/use-toast";
import { Save, ArrowLeft, ExternalLink, Eye, X, AlertTriangle } from "lucide-react";
import { PartnerPageAnalytics } from "./PartnerPageAnalytics";
import { BannerSection } from "@/components/partner/BannerSection";
import { HeroSection } from "@/components/partner/HeroSection";
import { AboutSection } from "@/components/partner/AboutSection";
import { BenefitsSection } from "@/components/partner/BenefitsSection";
import { CTASection } from "@/components/partner/CTASection";
import { CountdownSection } from "@/components/partner/CountdownSection";

interface ButtonData {
  text: string;
  url: string;
  variant: "primary" | "outline";
}

interface SectionData {
  id: string;
  section_type: string;
  title: string;
  subtitle: string;
  content: string;
  youtube_url: string;
  image_url: string;
  background_color: string;
  buttons: ButtonData[];
  is_visible: boolean;
  order_index: number;
}

interface PartnerPageEditorProps {
  pageId: string;
  onBack: () => void;
}

export function PartnerPageEditor({ pageId, onBack }: PartnerPageEditorProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: page } = useQuery({
    queryKey: ["admin-partner-page", pageId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("partner_landing_pages")
        .select("*")
        .eq("id", pageId)
        .single();
      if (error) throw error;
      return data;
    },
  });

  const { data: sections = [] } = useQuery({
    queryKey: ["admin-partner-sections", pageId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("partner_landing_sections")
        .select("*")
        .eq("landing_page_id", pageId)
        .order("order_index");
      if (error) throw error;
      return (data as unknown as SectionData[]) || [];
    },
  });

  const [pageTitle, setPageTitle] = useState("");
  const [metaDesc, setMetaDesc] = useState("");
  const [isActive, setIsActive] = useState(false);
  const [editedSections, setEditedSections] = useState<SectionData[]>([]);
  const [initialized, setInitialized] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  // Sync state when data loads
  if (page && sections.length > 0 && !initialized) {
    setPageTitle(page.page_title || "");
    setMetaDesc(page.meta_description || "");
    setIsActive(page.is_active || false);
    setEditedSections(sections);
    setInitialized(true);
  }

  const saveMutation = useMutation({
    mutationFn: async () => {
      // Save page metadata
      const { error: pageError } = await supabase
        .from("partner_landing_pages")
        .update({
          page_title: pageTitle,
          meta_description: metaDesc,
          is_active: isActive,
        })
        .eq("id", pageId);
      if (pageError) throw pageError;

      // Save each section
      for (const section of editedSections) {
        const { error } = await supabase
          .from("partner_landing_sections")
          .update({
            title: section.title,
            subtitle: section.subtitle,
            content: section.content,
            youtube_url: section.youtube_url,
            image_url: section.image_url,
            background_color: section.background_color,
            buttons: section.buttons as any,
            is_visible: section.is_visible,
          })
          .eq("id", section.id);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-partner"] });
      queryClient.invalidateQueries({ queryKey: ["partner-"] });
      queryClient.invalidateQueries({ queryKey: ["active-partner-pages"] });
      toast({ title: "Salvo!", description: "Landing page atualizada com sucesso." });
    },
    onError: () => {
      toast({ title: "Erro", description: "Não foi possível salvar.", variant: "destructive" });
    },
  });

  const updateSection = (index: number, updated: SectionData) => {
    setEditedSections(prev => prev.map((s, i) => (i === index ? updated : s)));
  };

  if (!page) return null;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={onBack}>
          <ArrowLeft className="h-4 w-4 mr-2" /> Voltar
        </Button>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setShowPreview(true)}>
            <Eye className="h-4 w-4 mr-2" /> Pré-visualizar
          </Button>
          <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
            <Save className="h-4 w-4 mr-2" />
            {saveMutation.isPending ? "Salvando..." : "Salvar Tudo"}
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Configurações da Página</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 border rounded-lg bg-muted/30">
            <div>
              <p className="font-medium">Página ativa</p>
              <p className="text-sm text-muted-foreground">
                {isActive ? "Visível para candidatos" : "Oculta para candidatos"}
              </p>
            </div>
            <Switch checked={isActive} onCheckedChange={setIsActive} />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label>Título da página</Label>
              <Input value={pageTitle} onChange={e => setPageTitle(e.target.value)} />
            </div>
            <div>
              <Label>Slug</Label>
              <div className="flex items-center gap-2">
                <Input value={page.slug} disabled className="bg-muted" />
                {isActive && (
                  <Button variant="outline" size="icon" asChild>
                    <a href={`/candidate/jobs/community/parceiros/${page.slug}`} target="_blank">
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  </Button>
                )}
              </div>
            </div>
          </div>

          <div>
            <Label>Meta descrição (SEO)</Label>
            <Input value={metaDesc} onChange={e => setMetaDesc(e.target.value)} />
          </div>
        </CardContent>
      </Card>

      <PartnerPageAnalytics pageId={pageId} />

      {editedSections.map((section, i) => (
        <SectionEditor
          key={section.id}
          section={section}
          onChange={updated => updateSection(i, updated)}
        />
      ))}
      {/* Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-[100vw] w-screen h-screen max-h-screen p-0 rounded-none border-none overflow-y-auto">
          <div className="sticky top-0 z-50 flex items-center justify-between px-4 py-2 bg-background border-b">
            <div className="flex items-center gap-2">
              <Eye className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium text-sm">Modo Preview</span>
              {!isActive && (
                <span className="flex items-center gap-1 text-xs text-destructive bg-destructive/10 px-2 py-0.5 rounded">
                  <AlertTriangle className="h-3 w-3" /> Página ainda não publicada
                </span>
              )}
            </div>
            <Button variant="ghost" size="icon" onClick={() => setShowPreview(false)}>
              <X className="h-4 w-4" />
            </Button>
          </div>
          <div>
            {editedSections
              .filter(s => s.is_visible)
              .sort((a, b) => a.order_index - b.order_index)
              .map(section => {
                const props = {
                  title: section.title,
                  subtitle: section.subtitle,
                  content: section.content,
                  buttons: section.buttons,
                  backgroundColor: section.background_color,
                };
                switch (section.section_type) {
                  case "banner":
                    return <BannerSection key={section.id} imageUrl={section.image_url} buttons={section.buttons} backgroundColor={section.background_color} />;
                  case "countdown":
                    return <CountdownSection key={section.id} title={section.title} targetDate={section.content} backgroundColor={section.background_color} />;
                  case "hero":
                    return <HeroSection key={section.id} {...props} youtubeUrl={section.youtube_url} />;
                  case "about":
                    return <AboutSection key={section.id} {...props} imageUrl={section.image_url} />;
                  case "benefits":
                    return <BenefitsSection key={section.id} {...props} />;
                  case "cta":
                    return <CTASection key={section.id} {...props} />;
                  default:
                    return null;
                }
              })}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
